import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Eye, Filter, X, RotateCcw, CreditCard, Search, ChevronDown, ChevronUp, Loader } from 'lucide-react'
import { collection, getDocs, query, orderBy, limit, startAfter, Timestamp } from 'firebase/firestore'
import { db, COLLECTIONS } from '../firebase/config'
import PageHelpButton from '../components/PageHelpButton'
import toast from 'react-hot-toast'

const PAGE_SIZE = 50

const ManualPayments = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [lastVisible, setLastVisible] = useState(null)
  const [hasMore, setHasMore] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [activePayment, setActivePayment] = useState(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [filterDate, setFilterDate] = useState('')
  const [filterName, setFilterName] = useState('')
  const [filterPhone, setFilterPhone] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)

  const getDateStr = (p) => {
    const d = p.date || p.createdAt
    if (!d) return ''
    if (d && typeof d.toDate === 'function') return d.toDate().toISOString().split('T')[0]
    return typeof d === 'string' ? d.split('T')[0] : ''
  }
  const filtered = payments.filter(p => {
    const q = (searchQuery || '').trim().toLowerCase()
    if (q) {
      const match = (s) => (s || '').toLowerCase().includes(q)
      if (!match(p.name) && !match(p.phone) && !match(p.transactionRef) && !match(String(p.amount))) return false
    }
    const byDate = !filterDate || getDateStr(p) === filterDate
    const byName = !filterName || (p.name || '').toLowerCase().includes((filterName || '').toLowerCase())
    const byPhone = !filterPhone || (p.phone || '').includes(filterPhone)
    return byDate && byName && byPhone
  })

  const openPreview = (payment) => {
    setActivePayment(payment)
    setShowModal(true)
  }

  const fetchManualPayments = async (cursor = null, append = false) => {
    try {
      if (append) setLoadingMore(true)
      else setLoading(true)
      const paymentsRef = collection(db, COLLECTIONS.MANUAL_PAYMENTS)
      const q = cursor
        ? query(paymentsRef, orderBy('createdAt', 'desc'), limit(PAGE_SIZE), startAfter(cursor))
        : query(paymentsRef, orderBy('createdAt', 'desc'), limit(PAGE_SIZE))
      const snapshot = await getDocs(q)
      const paymentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      const lastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      setLastVisible(lastDoc)
      setHasMore(snapshot.docs.length === PAGE_SIZE)
      if (append) setPayments(prev => [...prev, ...paymentsData])
      else setPayments(paymentsData)
    } catch (error) {
      console.error('Error fetching manual payments:', error)
      toast.error('Failed to load manual payments')
      setHasMore(false)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }

  const loadMorePayments = () => {
    if (!lastVisible || loadingMore) return
    fetchManualPayments(lastVisible, true)
  }

  useEffect(() => {
    fetchManualPayments()
  }, [])

  // Format date for display
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A'
    let date
    if (dateValue instanceof Timestamp) {
      date = dateValue.toDate()
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue)
    } else {
      date = dateValue
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  
  // Get date value for a payment (prioritize date field, fallback to createdAt)
  const getPaymentDate = (payment) => {
    return payment.date || payment.createdAt
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-400">Loading manual payments...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manual Balance Payments</h1>
          <p className="text-gray-400 mt-1">View and manage manual balance payments</p>
        </div>
        <button
          onClick={() => fetchManualPayments()}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      <PageHelpButton
        videoId={4}
        title="Manual Balance Payments tutorial"
        youtubeUrl="https://www.youtube.com/embed/mbqpAVeR1yU"
      />

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, amount, transaction ref..."
            className="w-full pl-9 pr-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {searchQuery.trim() && <span className="text-sm text-gray-400">{filtered.length} result(s)</span>}
      </div>

      {/* Filters (dropdown) */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <button
          type="button"
          onClick={() => setFilterOpen(prev => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-200">Filters</span>
            {(filterDate || filterName || filterPhone) && <span className="text-xs text-blue-300">(active)</span>}
          </div>
          {filterOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>
        {filterOpen && (
          <div className="border-t border-gray-700 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Date</label>
                <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Name</label>
                <input type="text" value={filterName} onChange={(e) => setFilterName(e.target.value)} placeholder="Filter by name" className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Phone</label>
                <input type="text" value={filterPhone} onChange={(e) => setFilterPhone(e.target.value)} placeholder="Filter by phone" className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
              </div>
            </div>
            {(filterDate || filterName || filterPhone) && (
              <button onClick={() => { setFilterDate(''); setFilterName(''); setFilterPhone('') }} className="mt-2 text-sm text-blue-400 hover:text-blue-300">Clear filters</button>
            )}
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date & Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Transaction Ref</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                    {payments.length === 0 ? 'No manual payments found' : 'No payments match the filters'}
                  </td>
                </tr>
              ) : (
                filtered.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-700 bg-opacity-50">
                    <td className="px-2 py-4 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => openPreview(payment)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg text-blue-400 hover:text-blue-300 hover:bg-gray-700/80 transition-colors touch-manipulation"
                        title="View details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(getPaymentDate(payment))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{payment.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{payment.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                      GHS {Number(payment.amount).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-mono">
                      {payment.transactionRef || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Completed
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && payments.length > 0 && (
          <div className="border-t border-gray-700 px-6 py-3 flex items-center justify-between bg-gray-800/50">
            <p className="text-xs text-gray-400">Load more only when needed to reduce reads.</p>
            <button
              type="button"
              onClick={loadMorePayments}
              disabled={!hasMore || loadingMore}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : hasMore ? (
                <span>Load previous payments</span>
              ) : (
                <span>No more</span>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Preview Modal - portaled so header is below top bar */}
      {showModal && activePayment && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-start justify-center p-4 pt-20 overflow-y-auto">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-5rem)] overflow-y-auto my-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Payment Details</h2>
              <button
                onClick={() => {
                  setShowModal(false)
                  setActivePayment(null)
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Payment Info */}
              <div className="bg-gray-700 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                  <CreditCard className="w-5 h-5" />
                  <span>Payment Information</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Payment ID</p>
                    <p className="text-white font-mono text-sm">{activePayment.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Date & Time</p>
                    <p className="text-white">{formatDate(getPaymentDate(activePayment))}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Name</p>
                    <p className="text-white font-semibold">{activePayment.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Phone</p>
                    <p className="text-white">{activePayment.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Amount</p>
                    <p className="text-white text-xl font-bold">GHS {Number(activePayment.amount).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Transaction Reference</p>
                    <p className="text-white font-mono text-sm">{activePayment.transactionRef || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Payment Metadata */}
              {activePayment.metadata && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Payment Metadata</h3>
                  <div className="space-y-2">
                    {activePayment.metadata.type && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Type:</span>
                        <span className="text-white">{activePayment.metadata.type}</span>
                      </div>
                    )}
                    {activePayment.metadata.customerName && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Customer Name:</span>
                        <span className="text-white">{activePayment.metadata.customerName}</span>
                      </div>
                    )}
                    {activePayment.metadata.customerPhone && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Customer Phone:</span>
                        <span className="text-white">{activePayment.metadata.customerPhone}</span>
                      </div>
                    )}
                    {activePayment.metadata.amount && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Amount:</span>
                        <span className="text-white">GHS {Number(activePayment.metadata.amount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default ManualPayments

